import React, { useEffect } from 'react'
import { Button } from 'antd'
import { Formik } from 'formik'
import { Form, Input, Select, TreeSelect } from 'formik-antd'
import FormikDatePicker from '@/ui/datepicker/FormikDatePicker'
import { useSelector, useDispatch } from 'react-redux'
import {
  listBoxAlertPage,
  listBoxFollowUpCategory,
  listBoxFollowUpStatus,
  listBoxFollowUpAssignUser,
} from 'appRedux/actions/Listboxs'
import dayjs from 'dayjs'
import IntlMessages from 'util/IntlMessages'

const { Option } = Select
const { TreeNode } = TreeSelect

interface FollowUpFormProps {
  validationSchema: any
  initialValue: any
  handleSubmit: (values: any, helpers: any) => void
  handleCloseForm: () => void
  loader?: boolean
}

const FollowUpForm: React.FC<FollowUpFormProps> = ({
  validationSchema,
  initialValue,
  handleSubmit,
  handleCloseForm,
  loader = false,
}) => {
  const { auth: { authUser } = { authUser: undefined }, listBoxs, systemConfig } = useSelector(
    (state: any) => state,
  )
  const listBoxState = (listBoxs || {}) as any
  const followUpCategoryList: any[] = listBoxState.followUpCategoryList || []
  const followUpStatusList: any[] = listBoxState.followUpStatusList || []
  const followUpAssignUserList: any[] = listBoxState.followUpAssignUserList || []
  const alertPageList: any[] = listBoxState.alertPageList || []
  const system = ((systemConfig || {}) as any)?.dataSource?.SYSTEM || {}
  const { SYSTEM_DATE } = system

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(listBoxAlertPage())
    dispatch(listBoxFollowUpCategory())
    dispatch(listBoxFollowUpStatus())
    dispatch(listBoxFollowUpAssignUser(authUser))
  }, [dispatch, authUser])

  const formItemLayout = {
    labelCol: { sm: { span: 8 }, md: { span: 8 }, lg: { span: 8 } },
    wrapperCol: { sm: { span: 12 }, md: { span: 12 }, lg: { span: 12 } },
    colon: false,
  }

  useEffect(() => {
    try {
      if (!initialValue || initialValue.follow_topic) return
      const el = document.querySelector<HTMLInputElement>('input[name="follow_topic"]')
      if (el) {
        el.focus()
      }
    } catch (e) {
      // ignore focus errors
    }
    // run once on mount to avoid stealing focus later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Formik
        enableReinitialize
        isSubmitting
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
      >
        <Form {...formItemLayout}>
          <Form.Item name="follow_topic" label={<IntlMessages id="app.global.label.topic" />}>
            <Input name="follow_topic" placeholder="Topic" />
          </Form.Item>
          <Form.Item name="follow_message" label={<IntlMessages id="app.global.label.message" />}>
            <Input.TextArea name="follow_message" placeholder="Message" rows={4} />
          </Form.Item>
          <Form.Item name="category" label={<IntlMessages id="app.global.label.category" />}>
            <Select
              name="category"
              allowClear
              placeholder="Select Category"
              showSearch
              filterOption={(input: string, option: any) =>
                (option?.props?.children ?? '').toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {followUpCategoryList.map((e: any) => (
                <Option key={`category-${e.follow_up_category_id}`} value={e.follow_up_category_id}>
                  {e.follow_up_category_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label={<IntlMessages id="app.global.label.status" />}>
            <Select
              name="status"
              allowClear
              placeholder="Select status"
              showSearch
              filterOption={(input: string, option: any) =>
                (option?.props?.children ?? '').toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {followUpStatusList.map((e: any) => (
                <Option key={`statusList-${e.follow_up_status_id}`} value={e.follow_up_status_id}>
                  {e.follow_up_status_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="assign_user" label={<IntlMessages id="app.global.label.assignUser" />}>
            <TreeSelect
              name="assign_user"
              treeCheckable
              treeDefaultExpandAll={false}
              placeholder="Assign User"
              showSearch
              multiple
              filterTreeNode={(input: string, option: any) =>
                (option?.props?.title ?? '').toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {followUpAssignUserList.map((group: any) => (
                <TreeNode value={group.group_name} title={group.group_name} key={`${group.group_name}`}>
                  {(group.user_list || []).map((user: any) => (
                    <TreeNode value={user.user_name} title={user.user_name} key={`${user.user_name}`} />
                  ))}
                </TreeNode>
              ))}
            </TreeSelect>
          </Form.Item>

          <Form.Item name="alert_location" label={<IntlMessages id="app.global.label.alert" />}>
            <Select
              mode="multiple"
              name="alert_location"
              allowClear
              placeholder="Select Location"
              showSearch
              filterOption={(input: string, option: any) =>
                (option?.props?.children ?? '').toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {alertPageList
                .filter((obj: any) => obj.alert_page_id !== 6)
                .map((e: any) => (
                  <Option key={`locationList-${e.alert_page_id}`} value={e.alert_page_id}>
                    {e.alert_page_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="alert_date" label={<IntlMessages id="app.global.label.date" />}>
            <FormikDatePicker
              name="alert_date"
              format="DD/MM/YYYY"
              disabledDate={(current) =>
                !!current && dayjs(current as any).isBefore(dayjs(SYSTEM_DATE), 'day')
              }
              placeholder="Alert date"
            />
          </Form.Item>

          <Button
            style={{ marginBottom: 0, marginLeft: '35%' }}
            type="default"
            htmlType="button"
            onClick={() => handleCloseForm()}
          >
            <IntlMessages id="app.global.bt.back" />
          </Button>
          <Button style={{ marginBottom: 0 }} type="primary" htmlType="submit" loading={loader}>
            <IntlMessages id="app.global.bt.submit" />
          </Button>
        </Form>
      </Formik>
    </>
  )
}

export default FollowUpForm
